/* eslint-disable max-lines-per-function */
import {
  Antrag,
  Arbeitsbereich,
  Mitarbeiter,
} from '@/domain/arbeitszeiten/entities';
import { GET_ANTRAG_BY_ID } from '@/domain/arbeitszeiten/hooks/graphql/queries';
import {
  Arbeitszeit_GetAntragByIdQuery,
  Arbeitszeit_GetAntragByIdQueryVariables,
  Markierung,
} from '@/gql/graphql';
import { getDateOrNull } from '@/utils/helperFunctions';
import { ApolloError, useQuery } from '@apollo/client';

export function useAntrag({
  antragId,
  standortId,
  datum,
}: {
  antragId: string;
  standortId: string;
  datum: string;
}): { data: Antrag | null; error: ApolloError | null; loading: boolean } {
  const { data, error, loading } = useQuery<
    Arbeitszeit_GetAntragByIdQuery,
    Arbeitszeit_GetAntragByIdQueryVariables
  >(GET_ANTRAG_BY_ID, {
    variables: {
      antragid: antragId,
      standortId: standortId,
      datum: datum,
    },
    skip: !antragId,
  });

  if (loading) return { data: null, error: null, loading };
  if (error) return { data: null, error, loading };

  const a = data?.arbeitszeiten_antrag;
  if (!a) return null;

  const antrag = new Antrag(
    a.id,
    a.status,
    a.arbeitszeitId,
    a.offen,
    a.bemerkungAntragsteller,
    a.bemerkungVorgesetzter,
    getDateOrNull(a.datum),
    getDateOrNull(a.aenderungsdatum),
    getDateOrNull(a.erstellungsdatum),
    new Arbeitsbereich(
      a.arbeitsbereich.name,
      a.arbeitsbereich.id,
      a.arbeitsbereich.markierung as unknown as Markierung,
      [],
    ),
    new Mitarbeiter(
      a.mitarbeiter.vorname,
      a.mitarbeiter.nachname,
      a.mitarbeiter.id,
      false,
      a.mitarbeiter.bildUrl,
    ),
    {
      time: getDateOrNull(a.beginn),
      isChangeRequested: a.arbeitszeitId === null,
    },
    {
      time: getDateOrNull(a.ende),
      isChangeRequested: a.arbeitszeitId === null,
    },
    {
      time: getDateOrNull(a.beginn2.time),
      isChangeRequested: a.beginn2.isChangeRequested,
    },
    {
      time: getDateOrNull(a.ende2.time),
      isChangeRequested: a.ende2.isChangeRequested,
    },
  );

  const historie = a.antragsistorie.map(
    (resultAntrag) =>
      new Antrag(
        resultAntrag.id,
        resultAntrag.status,
        resultAntrag.arbeitszeitId,
        resultAntrag.offen,
        resultAntrag.bemerkungAntragsteller,
        resultAntrag.bemerkungVorgesetzter,
        getDateOrNull(resultAntrag.datum),
        getDateOrNull(resultAntrag.aenderungsdatum),
        getDateOrNull(a.erstellungsdatum),
        new Arbeitsbereich(
          resultAntrag.arbeitsbereich.name,
          resultAntrag.arbeitsbereich.id,
          resultAntrag.arbeitsbereich.markierung as unknown as Markierung,
          [],
        ),
        new Mitarbeiter(
          resultAntrag.mitarbeiter.vorname,
          resultAntrag.mitarbeiter.nachname,
          resultAntrag.mitarbeiter.id,
          false,
          resultAntrag.mitarbeiter.bildUrl,
        ),
        {
          time: getDateOrNull(resultAntrag.beginn),
          isChangeRequested: resultAntrag.arbeitszeitId === null,
        },
        {
          time: getDateOrNull(resultAntrag.ende),
          isChangeRequested: resultAntrag.arbeitszeitId === null,
        },
        {
          time: getDateOrNull(resultAntrag.beginn2.time),
          isChangeRequested: resultAntrag.beginn2.isChangeRequested,
        },
        {
          time: getDateOrNull(resultAntrag.ende2.time),
          isChangeRequested: resultAntrag.ende2.isChangeRequested,
        },
      ),
  );
  antrag.historie = historie;
  return { data: antrag, error: null, loading };
}
